const DB_NAME = 'forest-river-offline'

/**
 * Normalizes vehicle ids for consistent IndexedDB keys
 *
 * @param {string|number} vehicleId vehicle identifier
 * @returns {string|null}
 */
function normalizeVehicleId(vehicleId) {
  if (vehicleId == null || vehicleId === '') return null
  return String(vehicleId)
}

/**
 * Reads a vehicle record, trying string and numeric key variants
 *
 * @param {object} db IndexedDB database
 * @param {string} storeName object store name
 * @param {string|number} vehicleId vehicle identifier
 * @returns {Promise<object|undefined>}
 */
async function dbGetVehicleRecord(db, storeName, vehicleId) {
  const id = normalizeVehicleId(vehicleId)
  if (!id) return undefined

  let result = await dbGet(db, storeName, id)
  if (!result && vehicleId != null && typeof vehicleId !== 'string') {
    result = await dbGet(db, storeName, vehicleId)
  }
  if (!result && !Number.isNaN(Number(id))) {
    result = await dbGet(db, storeName, Number(id))
  }

  return result
}
const DB_VERSION = 1
const STORE_VEHICLE_META = 'vehicleMeta'
const STORE_HTML_ASSETS = 'htmlAssets'
const STORE_CONTENTS = 'contents'
const PDF_CACHE_NAME = 'ok-offline-pdfs'
const HERO_SHOT_CACHE_NAME = 'ok-offline-hero-shots'

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = event => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(STORE_VEHICLE_META)) {
        db.createObjectStore(STORE_VEHICLE_META, { keyPath: 'vehicleId' })
      }
      if (!db.objectStoreNames.contains(STORE_HTML_ASSETS)) {
        db.createObjectStore(STORE_HTML_ASSETS, { keyPath: 'objectKey' })
      }
      if (!db.objectStoreNames.contains(STORE_CONTENTS)) {
        db.createObjectStore(STORE_CONTENTS, { keyPath: 'vehicleId' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function dbPut(db, storeName, data) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite')
    tx.objectStore(storeName).put(data)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

function dbGet(db, storeName, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly')
    const req = tx.objectStore(storeName).get(key)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function dbDelete(db, storeName, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite')
    tx.objectStore(storeName).delete(key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

function dbGetAll(db, storeName) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly')
    const req = tx.objectStore(storeName).getAll()
    req.onsuccess = () => resolve(req.result || [])
    req.onerror = () => reject(req.error)
  })
}

/**
 * Stores an HTML asset body in IndexedDB
 *
 * @param {string} objectKey asset identifier
 * @param {string} html HTML content
 * @returns {Promise<void>}
 */
export async function saveHtmlAsset(objectKey, html) {
  const db = await openDB()
  await dbPut(db, STORE_HTML_ASSETS, { objectKey, html, savedAt: Date.now() })
  db.close()
}

/**
 * Retrieves a cached HTML asset
 *
 * @param {string} objectKey asset identifier
 * @returns {Promise<string|null>} cached HTML or null
 */
export async function getHtmlAsset(objectKey) {
  const db = await openDB()
  const result = await dbGet(db, STORE_HTML_ASSETS, objectKey)
  db.close()
  return result ? result.html : null
}

/**
 * Caches a PDF URL into the Cache API
 *
 * @param {string} url PDF URL to cache
 * @returns {Promise<void>}
 */
export async function cachePdfUrl(url) {
  if (!url) return
  try {
    const cache = await caches.open(PDF_CACHE_NAME)
    const existing = await matchCachedPdfResponse(cache, url)
    if (!existing) {
      await cache.add(url)
    }
  } catch (e) {
    console.warn('[offline] Failed to cache PDF:', url, e)
  }
}

/**
 * Finds a cached PDF response, tolerating query-string differences
 *
 * @param {Cache} cache PDF cache store
 * @param {string} url original PDF URL
 * @returns {Promise<Response|undefined>}
 */
async function matchCachedPdfResponse(cache, url) {
  let cached = await cache.match(url)
  if (cached) return cached

  cached = await cache.match(url, { ignoreSearch: true })
  if (cached) return cached

  const keys = await cache.keys()
  const target = new URL(url)
  const matchingRequest = keys.find(request => {
    try {
      const candidate = new URL(request.url)
      return candidate.origin === target.origin && candidate.pathname === target.pathname
    } catch (e) {
      return false
    }
  })

  if (matchingRequest) {
    return cache.match(matchingRequest)
  }

  return undefined
}

/**
 * Returns cached PDF bytes when available
 *
 * @param {string} url original PDF URL
 * @returns {Promise<ArrayBuffer|null>}
 */
export async function getCachedPdfData(url) {
  if (!url) return null

  try {
    const cache = await caches.open(PDF_CACHE_NAME)
    const cached = await matchCachedPdfResponse(cache, url)
    if (!cached) return null

    return cached.arrayBuffer()
  } catch (e) {
    console.warn('[offline] Failed to read cached PDF data:', url, e)
    return null
  }
}

/**
 * Returns a blob URL for a cached PDF when available
 *
 * @param {string} url original PDF URL
 * @returns {Promise<string>} blob URL or original URL
 */
export async function getCachedPdfBlobUrl(url) {
  if (!url) return url

  try {
    const data = await getCachedPdfData(url)
    if (!data) return url

    return URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
  } catch (e) {
    console.warn('[offline] Failed to read cached PDF:', url, e)
    return url
  }
}

/**
 * Caches a hero shot image URL into the Cache API
 *
 * @param {string} url hero shot image URL
 * @returns {Promise<void>}
 */
export async function cacheHeroShotUrl(url) {
  if (!url) return
  try {
    const response = await fetch(url)
    if (!response.ok) return

    const cache = await caches.open(HERO_SHOT_CACHE_NAME)
    await cache.put(url, response.clone())
  } catch (e) {
    console.warn('[offline] Failed to cache hero shot:', url, e)
  }
}

/**
 * Opens the hero shot Cache API store
 *
 * @returns {Promise<Cache>}
 */
export async function openHeroShotCache() {
  return caches.open(HERO_SHOT_CACHE_NAME)
}

/**
 * Saves the full vehicle content structure to IndexedDB
 *
 * @param {string} vehicleId vehicle identifier
 * @param {object} contentsSnapshot serialized content data
 * @param {object} [vehicleInfo] vehicle metadata for offline vehicle selector
 * @returns {Promise<void>}
 */
export async function saveVehicleContents(vehicleId, contentsSnapshot, vehicleInfo = null) {
  const id = normalizeVehicleId(vehicleId)
  if (!id) return

  const db = await openDB()
  const existing = await dbGetVehicleRecord(db, STORE_CONTENTS, id)

  await dbPut(db, STORE_CONTENTS, {
    vehicleId: id,
    data: contentsSnapshot,
    vehicleInfo: vehicleInfo || existing?.vehicleInfo || null,
    savedAt: Date.now(),
  })
  db.close()
}

/**
 * Retrieves stored vehicle contents for offline rendering
 *
 * @param {string} vehicleId vehicle identifier
 * @returns {Promise<object|null>} stored content data or null
 */
export async function getVehicleContents(vehicleId) {
  const id = normalizeVehicleId(vehicleId)
  if (!id) return null

  const db = await openDB()
  const result = await dbGetVehicleRecord(db, STORE_CONTENTS, id)
  db.close()
  return result ? result.data : null
}

/**
 * Saves download metadata for a vehicle
 *
 * @param {string} vehicleId vehicle identifier
 * @param {string} downloadType 'all-content' or 'owners-manual'
 * @param {Array} downloadedItems list of downloaded category titles
 * @param {object} [assetRefs] cached asset references
 * @param {object} [vehicleInfo] vehicle metadata for offline vehicle selector
 * @param {object} [heroShot] cached hero shot metadata
 * @returns {Promise<void>}
 */
export async function saveDownloadMeta(
  vehicleId,
  downloadType,
  downloadedItems,
  assetRefs = {},
  vehicleInfo = null,
  heroShot = null
) {
  const id = normalizeVehicleId(vehicleId)
  if (!id) return

  const db = await openDB()
  const existing = await dbGetVehicleRecord(db, STORE_VEHICLE_META, id)

  const meta = existing || { vehicleId: id, downloads: {} }
  meta.vehicleId = id
  meta.downloads[downloadType] = {
    items: downloadedItems,
    objectKeys: assetRefs.objectKeys || [],
    pdfUrls: assetRefs.pdfUrls || [],
    pdfs: assetRefs.pdfs || [],
    downloadedAt: Date.now(),
  }
  meta.lastUpdated = Date.now()
  if (vehicleInfo) {
    meta.vehicleInfo = vehicleInfo
  }
  if (heroShot) {
    meta.heroShot = heroShot
  }

  await dbPut(db, STORE_VEHICLE_META, meta)
  db.close()
}

/**
 * Returns download metadata for a vehicle
 *
 * @param {string} vehicleId vehicle identifier
 * @returns {Promise<object|null>} metadata or null
 */
export async function getDownloadMeta(vehicleId) {
  const id = normalizeVehicleId(vehicleId)
  if (!id) return null

  const db = await openDB()
  const result = await dbGetVehicleRecord(db, STORE_VEHICLE_META, id)
  db.close()
  return result || null
}

/**
 * Checks whether a specific download type is available for a vehicle
 *
 * @param {string} vehicleId vehicle identifier
 * @param {string} downloadType 'all-content' or 'owners-manual'
 * @returns {Promise<boolean>} true if downloaded
 */
export async function isDownloaded(vehicleId, downloadType) {
  const meta = await getDownloadMeta(vehicleId)
  return !!(meta && meta.downloads && meta.downloads[downloadType])
}

/**
 * Returns the set of downloaded content category titles for marker display
 *
 * @param {string} vehicleId vehicle identifier
 * @returns {Promise<Set>} set of category title strings
 */
export async function getDownloadedCategories(vehicleId) {
  const meta = await getDownloadMeta(vehicleId)
  if (!meta || !meta.downloads) return new Set()

  const categories = new Set()

  if (meta.downloads['owners-manual']) {
    const items = meta.downloads['owners-manual'].items || []
    items.forEach(item => categories.add(item))
  }

  if (meta.downloads['all-content']) {
    const items = meta.downloads['all-content'].items || []
    items.forEach(item => categories.add(item))
  }

  if (meta.downloads['component-manual-pdfs']) {
    const pdfs = meta.downloads['component-manual-pdfs'].pdfs || []
    if (pdfs.length) {
      categories.add('Component Manual')
    }
  }

  return categories
}

/**
 * Deletes HTML assets from IndexedDB
 *
 * @param {Array<string>} objectKeys asset identifiers
 * @returns {Promise<void>}
 */
export async function deleteHtmlAssets(objectKeys) {
  if (!objectKeys || !objectKeys.length) return
  const db = await openDB()
  const tx = db.transaction(STORE_HTML_ASSETS, 'readwrite')
  const store = tx.objectStore(STORE_HTML_ASSETS)
  objectKeys.forEach(key => store.delete(key))
  await new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
  db.close()
}

/**
 * Removes a PDF URL from the offline cache
 *
 * @param {string} url PDF URL
 * @returns {Promise<void>}
 */
export async function uncachePdfUrl(url) {
  if (!url) return
  try {
    const cache = await caches.open(PDF_CACHE_NAME)
    await cache.delete(url)
  } catch (e) {
    console.warn('[offline] Failed to uncache PDF:', url, e)
  }
}

/**
 * Removes a hero shot image URL from the offline cache
 *
 * @param {string} url hero shot image URL
 * @returns {Promise<void>}
 */
export async function uncacheHeroShotUrl(url) {
  if (!url) return
  try {
    const cache = await caches.open(HERO_SHOT_CACHE_NAME)
    await cache.delete(url)
  } catch (e) {
    console.warn('[offline] Failed to uncache hero shot:', url, e)
  }
}

/**
 * Returns the current offline download state for a vehicle
 *
 * @param {string} vehicleId vehicle identifier
 * @returns {Promise<string>} 'none' | 'owners-manual' | 'all-content'
 */
export async function getOfflineDownloadState(vehicleId) {
  const meta = await getDownloadMeta(vehicleId)
  if (!meta || !meta.downloads) return 'none'
  if (meta.downloads['all-content']) return 'all-content'
  if (meta.downloads['owners-manual']) return 'owners-manual'
  return 'none'
}

/**
 * Returns whether any vehicle has offline content saved
 *
 * @returns {Promise<boolean>}
 */
export async function hasAnyOfflineContent() {
  const records = await getAllOfflineVehicleRecords()
  return records.length > 0
}

/**
 * Returns whether a vehicle has any offline downloads
 *
 * @param {string} vehicleId vehicle identifier
 * @returns {Promise<boolean>}
 */
export async function hasOfflineContent(vehicleId) {
  const meta = await getDownloadMeta(vehicleId)
  return !!(meta && meta.downloads && Object.keys(meta.downloads).length > 0)
}

/**
 * Returns all vehicles that have offline content saved
 *
 * @returns {Promise<Array>} offline vehicle records
 */
export async function getAllOfflineVehicleRecords() {
  const db = await openDB()
  const [metaRecords, contentRecords] = await Promise.all([
    dbGetAll(db, STORE_VEHICLE_META),
    dbGetAll(db, STORE_CONTENTS),
  ])
  db.close()

  const byId = new Map()

  metaRecords.forEach(meta => {
    if (meta.downloads && Object.keys(meta.downloads).length) {
      byId.set(meta.vehicleId, {
        vehicleId: meta.vehicleId,
        vehicleInfo: meta.vehicleInfo || null,
        downloads: meta.downloads,
        heroShot: meta.heroShot || null,
      })
    }
  })

  contentRecords.forEach(content => {
    const existing = byId.get(content.vehicleId)
    if (!existing) return

    existing.vehicleInfo = existing.vehicleInfo || content.vehicleInfo || null
    byId.set(content.vehicleId, existing)
  })

  return [...byId.values()]
}

/**
 * Removes a category from download metadata
 *
 * @param {string} vehicleId vehicle identifier
 * @param {string} downloadType download type key
 * @param {Array} items updated items list
 * @param {object} assetRefs remaining cached asset references
 * @returns {Promise<void>}
 */
export async function updateDownloadItems(vehicleId, downloadType, items, assetRefs) {
  if (!items.length) {
    await removeDownloadType(vehicleId, downloadType)
    return
  }
  await saveDownloadMeta(vehicleId, downloadType, items, assetRefs)
}

/**
 * Removes a download type entry from vehicle metadata
 *
 * @param {string} vehicleId vehicle identifier
 * @param {string} downloadType download type key
 * @returns {Promise<void>}
 */
export async function removeDownloadType(vehicleId, downloadType) {
  const meta = await getDownloadMeta(vehicleId)
  if (!meta || !meta.downloads || !meta.downloads[downloadType]) return

  delete meta.downloads[downloadType]
  meta.lastUpdated = Date.now()

  const db = await openDB()
  if (Object.keys(meta.downloads).length === 0) {
    await dbDelete(db, STORE_VEHICLE_META, vehicleId)
    await dbDelete(db, STORE_CONTENTS, vehicleId)
  } else {
    await dbPut(db, STORE_VEHICLE_META, meta)
  }
  db.close()
}

/**
 * Deletes all offline data for a vehicle including cached assets
 *
 * @param {string} vehicleId vehicle identifier
 * @returns {Promise<void>}
 */
export async function deleteOfflineData(vehicleId) {
  const meta = await getDownloadMeta(vehicleId)
  const objectKeys = new Set()
  const pdfUrls = new Set()
  const heroShotUrls = new Set()

  if (meta && meta.downloads) {
    Object.values(meta.downloads).forEach(download => {
      const keys = download.objectKeys || []
      const urls = download.pdfUrls || []
      keys.forEach(key => objectKeys.add(key))
      urls.forEach(url => pdfUrls.add(url))
      ;(download.pdfs || []).forEach(pdf => {
        if (pdf.url) pdfUrls.add(pdf.url)
      })
    })
  }

  if (meta?.heroShot?.urls) {
    Object.values(meta.heroShot.urls).forEach(url => heroShotUrls.add(url))
  }

  await deleteHtmlAssets([...objectKeys])
  await Promise.all([...pdfUrls].map(url => uncachePdfUrl(url)))
  await Promise.all([...heroShotUrls].map(url => uncacheHeroShotUrl(url)))

  const db = await openDB()
  await dbDelete(db, STORE_VEHICLE_META, vehicleId)
  await dbDelete(db, STORE_CONTENTS, vehicleId)
  db.close()
}
